import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiService } from '../../services/api';

export const fetchOrders = createAsyncThunk(
  'orders/fetchAll',
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiService.orders.getAll(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchOrderById = createAsyncThunk(
  'orders/fetchById',
  async ({ id, businessAccountId }, { rejectWithValue }) => {
    try {
      const response = await apiService.orders.getById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateOrderStatus = createAsyncThunk(
  'orders/updateStatus',
  async ({ id, status, note, businessAccountId }, { rejectWithValue }) => {
    try {
      const response = await apiService.orders.updateStatus(id, { status, note, businessAccountId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateOrderPayment = createAsyncThunk(
  'orders/updatePayment',
  async ({ id, paymentStatus, businessAccountId }, { rejectWithValue }) => {
    try {
      const response = await apiService.orders.updatePayment(id, { paymentStatus, businessAccountId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteOrder = createAsyncThunk(
  'orders/delete',
  async ({ id, businessAccountId }, { rejectWithValue }) => {
    try {
      await apiService.orders.delete(id, { businessAccountId });
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const generateOrderInvoice = createAsyncThunk(
  'orders/generateInvoice',
  async ({ id, businessAccountId }, { rejectWithValue }) => {
    try {
      const response = await apiService.orders.generateInvoice(id, { businessAccountId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchOrderInvoice = createAsyncThunk(
  'orders/fetchInvoice',
  async ({ id, businessAccountId }, { rejectWithValue }) => {
    try {
      const response = await apiService.orders.getInvoice(id, { businessAccountId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  items: [],
  currentOrder: null,
  currentOrderInvoice: null,
  loading: false,
  detailLoading: false,
  invoiceLoading: false,
  error: null,
  pagination: {
    total: 0,
    limit: 50,
    skip: 0,
    pages: 0,
  },
};

const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    clearCurrentOrder: (state) => {
      state.currentOrder = null;
      state.currentOrderInvoice = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setCurrentOrderInvoice: (state, action) => {
      state.currentOrderInvoice = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchOrders
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data || [];
        if (action.payload.pagination) {
          state.pagination = action.payload.pagination;
        }
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // fetchOrderById
      .addCase(fetchOrderById.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.currentOrder = action.payload.data || action.payload;
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.payload;
      })

      // updateOrderStatus
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        const updated = action.payload.data || action.payload;
        if (state.currentOrder && state.currentOrder._id === updated._id) {
          state.currentOrder = updated;
        }
        const idx = state.items.findIndex((o) => o._id === updated._id);
        if (idx !== -1) {
          state.items[idx] = updated;
        }
      })

      // updateOrderPayment
      .addCase(updateOrderPayment.fulfilled, (state, action) => {
        const updated = action.payload.data || action.payload;
        if (state.currentOrder && state.currentOrder._id === updated._id) {
          state.currentOrder = updated;
        }
        const idx = state.items.findIndex((o) => o._id === updated._id);
        if (idx !== -1) {
          state.items[idx] = updated;
        }
      })

      // deleteOrder
      .addCase(deleteOrder.fulfilled, (state, action) => {
        state.items = state.items.filter((o) => o._id !== action.payload);
      })

      // generateOrderInvoice
      .addCase(generateOrderInvoice.pending, (state) => {
        state.invoiceLoading = true;
        state.error = null;
      })
      .addCase(generateOrderInvoice.fulfilled, (state, action) => {
        state.invoiceLoading = false;
        state.currentOrderInvoice = action.payload.data || action.payload;
        // Update the order's invoiceId in both currentOrder and items list
        const invoice = action.payload.data || action.payload;
        if (state.currentOrder) {
          state.currentOrder = { ...state.currentOrder, invoiceId: invoice._id };
        }
      })
      .addCase(generateOrderInvoice.rejected, (state, action) => {
        state.invoiceLoading = false;
        state.error = action.payload;
      })

      // fetchOrderInvoice
      .addCase(fetchOrderInvoice.pending, (state) => {
        state.invoiceLoading = true;
      })
      .addCase(fetchOrderInvoice.fulfilled, (state, action) => {
        state.invoiceLoading = false;
        state.currentOrderInvoice = action.payload.data || action.payload;
      })
      .addCase(fetchOrderInvoice.rejected, (state) => {
        state.invoiceLoading = false;
        // Not an error if no invoice exists yet — just means none was generated
        state.currentOrderInvoice = null;
      });
  },
});

export const { clearCurrentOrder, clearError, setCurrentOrderInvoice } = ordersSlice.actions;
export default ordersSlice.reducer;
